import React, { useState, useEffect, useLayoutEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, TextField, Button, CircularProgress, Alert,
  Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Dialog,
  DialogTitle, DialogContent, DialogActions, DialogContentText
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import roleService from '../services/roleService';
import { PageTitleContainer } from '../components/common';

// Wrapper with page-level styling for sub-pages
const SubPageWrapper = ({ children }) => (
  <Box sx={{
    width: '100%',
    bgcolor: '#f5f6f7',
    minHeight: '100vh',
    pb: 4,
    px: 6,
    pt: 4,
    overflowX: 'hidden',
    backgroundImage: 'none',
    '&::before': { display: 'none' },
    '&::after': { display: 'none' }
  }}>
    {children}
  </Box>
);

const ManageRolesView = ({ onEdit }) => {
  const navigate = useNavigate();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [deleteDialog, setDeleteDialog] = useState({ open: false, role: null });
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => { loadRoles(); }, []);

  const loadRoles = async () => {
    setLoading(true);
    try {
      const response = await roleService.getAllRoles(null, search || null);
      setRoles(response.roles || []);
    } catch (err) {
      setError(typeof err === 'string' ? err : 'Failed to load roles');
    }
    setLoading(false);
  };

  const handleSearch = () => { loadRoles(); };

  const handleDelete = async () => {
    try {
      await roleService.deleteRole(deleteDialog.role.role_id || deleteDialog.role.id);
      setDeleteDialog({ open: false, role: null });
      setSuccess('Role deleted successfully');
      setTimeout(() => setSuccess(null), 3000);
      loadRoles();
    } catch (err) {
      const msg = typeof err === 'string' ? err : (err?.message || 'Failed to delete role');
      if (msg.includes('assigned to')) {
        // Fetch assigned users to show their names
        try {
          const res = await roleService.getRoleUsers(deleteDialog.role.role_id || deleteDialog.role.id);
          const users = res?.data || res?.users || [];
          const names = users.map(u => u.name || u.email).join(', ');
          setError(`${msg}${names ? ' Assigned to: ' + names : ''}`);
        } catch {
          setError(msg);
        }
      } else {
        setError(msg);
      }
      setDeleteDialog({ open: false, role: null });
    }
  };

  const handleClone = async (role) => {
    try {
      const cloneData = {
        name: `${role.name} (Copy)`,
        role_type: role.role_type || 'system',
        description: role.description || '',
        permissions: role.permissions || [],
      };
      const res = await roleService.createRole(cloneData);
      const clonedId = res?.role?.role_id || res?.data?.role_id || res?.role_id;
      setSuccess('Role cloned successfully');
      setTimeout(() => setSuccess(null), 3000);
      if (clonedId) {
        navigate(`/roles/create?edit=${clonedId}`);
      } else {
        loadRoles();
      }
    } catch (err) {
      setError(typeof err === 'string' ? err : (err?.message || 'Failed to clone role'));
    }
  };

  const filteredRoles = roles.filter(r =>
    !search || r.name.toLowerCase().includes(search.toLowerCase()) ||
    (r.description || '').toLowerCase().includes(search.toLowerCase())
  );
  // State for content height
  const [contentHeight, setContentHeight] = useState('calc(100vh - 230px)');
  // Calculate content height based on window size and header heights
  useLayoutEffect(() => {
    const updateContentHeight = () => {
      // Default header heights (fallback values if refs aren't available)
      const mainHeaderHeight = 73;  // Main app header
      const titleHeaderHeight = 80; // Create project header
      const extraPadding = 69;      // Additional padding for spacing

      const totalHeaderHeight = mainHeaderHeight + titleHeaderHeight + extraPadding;
      setContentHeight(`calc(100vh - ${totalHeaderHeight}px)`);
    };

    // Initial calculation
    updateContentHeight();

    // Re-calculate on window resize
    window.addEventListener('resize', updateContentHeight);

    return () => {
      window.removeEventListener('resize', updateContentHeight);
    };
  }, []);
  return (
    <Box>
      <PageTitleContainer
        title={'Manage role'}
        justifyContent='space-between'
        rightContent={

          <Button variant="contained" startIcon={<AddIcon />} onClick={onEdit}>Create role</Button>

        }
        sx={{
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
          ml: 0,
          mr: 0,
          mb: 2,
          mt: 0,
          py: '16px' // Exact 16px padding top and bottom
        }}
        titleProps={{
          sx: { fontFamily: "'Sofia Pro Bold', 'Sofia Pro', sans-serif", fontWeight: 700 }
        }}
      />

      <Paper sx={{
        borderRadius: '4px',
        p: 5,
        width: '100%',
        height: contentHeight,
        bgcolor: 'white',
        overflowX: 'hidden',
        overflowY: 'auto',
        boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
        backgroundImage: 'none',
        '&::before': {
          display: 'none'
        },
        '&::after': {
          display: 'none'
        }
      }}>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <TextField
            size="small" placeholder="Search roles..." fullWidth value={search}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSearch()}
            sx={{
              width: '100%',
              '& .MuiOutlinedInput-root': {
                borderRadius: '4px',
                color: 'var(--color-blue, #475969)',
                height: '45px',
                transition: 'all 0.2s',
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#475969',
                  borderWidth: '1px',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#475969',
                  borderWidth: '1px',
                },
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#4759694d',
                border: '1px solid #4759694d',
                borderRadius: '4px',
              },
              '& .MuiInputBase-input': {
                padding: '12px 14px',
                fontSize: '14px',
                color: 'var(--color-blue, #475969)',
              },
              '& .MuiInputBase-input::placeholder': {
                color: '#a2abb3',
                opacity: 0.7,
                fontSize: '14px',
              }
            }}
          />
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontStyle: "normal !important", fontSize: "16px !important", fontWeight: "bold !important", }}>Name</TableCell>
                  <TableCell sx={{ fontStyle: "normal !important", fontSize: "16px !important", fontWeight: "bold !important", }}>Permissions</TableCell>
                  <TableCell sx={{ fontStyle: "normal !important", fontSize: "16px !important", fontWeight: "bold !important", }}>Users</TableCell>
                  <TableCell sx={{ fontStyle: "normal !important", fontSize: "16px !important", fontWeight: "bold !important", }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRoles.length === 0 && (
                  <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }} variant="body2">No roles found</TableCell></TableRow>
                )}
                {filteredRoles.map(role => (
                  <TableRow key={role.id} hover sx={{
                    '&:hover': {
                      backgroundColor: 'rgba(0,0,0,0.03)'
                    },
                    '&:nth-of-type(even)': {
                      backgroundColor: 'white !important'
                    }
                  }}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>{role.name}</Typography>
                    </TableCell>
                    <TableCell variant="body2">
                      {role.permissions?.includes('*') ? 'All (*)' : `${role.permissions?.length || 0} permissions`}
                    </TableCell>
                    <TableCell variant="body2">{role.user_count || 0}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <IconButton size="small" title="Edit" onClick={() => navigate('/roles/create?edit=' + (role.role_id || role.id))}><EditIcon fontSize="small" /></IconButton>
                        <IconButton size="small" title="Clone" onClick={() => handleClone(role)}><ContentCopyIcon fontSize="small" /></IconButton>
                        <IconButton size="small" title="Delete" onClick={() => setDeleteDialog({ open: true, role })}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>


      {/* Delete Confirmation */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, role: null })}>
        <DialogTitle>Delete Role</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the role "{deleteDialog.role?.name}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, role: null })}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const ManageRolesPage = () => {
  const navigate = useNavigate();
  return <SubPageWrapper><ManageRolesView onEdit={() => navigate('/roles/create')} /></SubPageWrapper>;
};

export default ManageRolesPage;
export { ManageRolesView };
